import { debounce } from "es-toolkit";

export class SupersededError extends Error {
  constructor() {
    super("Superseded by a later call");
    this.name = "SupersededError";
  }
}

const DEFAULT_OPTIONS: Parameters<typeof debounce>[2] = { edges: ["leading", "trailing"] };

/**
 * Debounces a promise-returning function (on both edges by default)
 */
export function debouncedPromise<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
  debounceMs: number,
  debounceOptions = DEFAULT_OPTIONS,
): (...args: A) => Promise<R> {
  let pending: { resolve: (result: R) => void; reject: (reason: unknown) => void } | undefined;

  const run = debounce(
    (...args: A) => {
      const settle = pending;
      pending = undefined;
      if (!settle) return;

      fn(...args).then(settle.resolve, settle.reject);
    },
    debounceMs,
    debounceOptions,
  );

  return (...args: A) =>
    new Promise<R>((resolve, reject) => {
      pending?.reject(new SupersededError());
      pending = { resolve, reject };
      run(...args);
    });
}
